import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Printer,
  Search,
  Filter,
  Edit,
  DollarSign,
  AlertCircle,
  Eye,
} from "lucide-react";
import TablePagination from "@/components/masters/TablePagination";
import PrintReceiptDialog from "@/components/receipt/PrintReceiptDialog";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { MultiSelect } from "@/components/ui/multi-select";
import { SingleSelect } from "@/components/ui/single-select";
import api from "@/services/api";
import { format } from "date-fns";

// Define the collection record type based on new API response
interface CollectionRecord {
  id: string;
  organizationId: string;
  consumerId: string;
  collectionId: string;
  billingDate: string;
  dueDate: string;
  amount: number;
  totalPaid: number;
  status: string;
  entryNumber: string;
  notes: string;
  createdAt: string;
  updatedAt: string;
  consumer: {
    id: string;
    fullName: string;
    registrationNumber: string;
    contactNumber: string;
  };
  collection: {
    id: string;
    name: string;
    entryNumber: string;
  };
  payments: Payment[];
}

// Define the payment type
interface Payment {
  id: string;
  amount: number;
  paymentDate: string;
  transactionNumber: string;
  paymentMethod: string;
  description: string;
  collectedBy: {
    id: string;
    name: string;
    mobile: string;
  };
  creditAccount: {
    id: string;
    name: string;
  };
  debitAccount: {
    id: string;
    name: string;
  };
}

// Define consumer and collection types for dropdowns
interface Consumer {
  id: string;
  fullName: string;
  registrationNumber: string;
  contactNumber: string;
}

interface Collection {
  id: string;
  name: string;
  entryNumber: string;
}

const PaymentsPage = () => {
  const [collectionRecords, setCollectionRecords] = useState<
    CollectionRecord[]
  >([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [paymentsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    consumerId: "all",
    collectionId: "all",
    fromDate: "",
    toDate: "",
    status: "all",
  });
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Add state for edit modal
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [isEditPaymentMode, setIsEditPaymentMode] = useState(false);
  const [editingCollectionRecord, setEditingCollectionRecord] =
    useState<CollectionRecord | null>(null);
  const [selectedCollections, setSelectedCollections] = useState<{
    [key: string]: number;
  }>({});
  const [paymentAllocation, setPaymentAllocation] = useState<{
    [key: string]: number;
  }>({});
  const [selectedCashAccount, setSelectedCashAccount] = useState<string | null>(
    null
  );
  const [selectedBankAccount, setSelectedBankAccount] = useState<string | null>(
    null
  );
  const [cashAmount, setCashAmount] = useState<number>(0);
  const [bankAmount, setBankAmount] = useState<number>(0);

  // Add state for view modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingCollectionRecord, setViewingCollectionRecord] =
    useState<CollectionRecord | null>(null);

  // New state for consumers and collections
  const [consumers, setConsumers] = useState<Consumer[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [consumerOptions, setConsumerOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [collectionOptions, setCollectionOptions] = useState<
    { label: string; value: string }[]
  >([]);

  // Dummy data for the edit modal
  const dummyCollections = [
    {
      id: "1",
      name: "Water Bill - January 2024",
      service: "Water Supply",
      dueAmount: 150.0,
      dueDate: "2024-01-31",
    },
    {
      id: "2",
      name: "Sewage Bill - January 2024",
      service: "Sewage",
      dueAmount: 75.0,
      dueDate: "2024-01-31",
    },
  ];

  const dummyAccounts = {
    cashAccounts: [
      { id: "1", name: "Main Cash Account", balance: 5000.0 },
      { id: "2", name: "Petty Cash", balance: 1000.0 },
    ],
    bankAccounts: [
      { id: "1", name: "Main Bank Account", balance: 25000.0 },
      { id: "2", name: "Secondary Bank Account", balance: 15000.0 },
    ],
  };

  // Initial filter state for reset
  const initialFilters = {
    consumerId: "all",
    collectionId: "all",
    fromDate: "",
    toDate: "",
    status: "all",
  };

  // Fetch consumers and collections on mount
  useEffect(() => {
    const fetchConsumersAndCollections = async () => {
      try {
        // Get all consumers (no status filter to get all)
        const consumersRes = await api.consumer.getConsumers({ limit: 1000 });
        const consumersArr = Array.isArray(consumersRes.docs)
          ? consumersRes.docs
          : Array.isArray(consumersRes.data)
          ? consumersRes.data
          : [];
        setConsumers(consumersArr as Consumer[]);

        // Create options for consumer dropdown
        const consumerOpts = consumersArr.map((consumer: Consumer) => ({
          label: `${consumer.fullName} (${consumer.registrationNumber})`,
          value: consumer.id,
        }));
        setConsumerOptions(consumerOpts);

        // Get all collections (no status filter to get all)
        const collectionsRes = await api.collection.getCollections({
          limit: 1000,
        });
        const collectionsArr = Array.isArray(collectionsRes.docs)
          ? collectionsRes.docs
          : Array.isArray(collectionsRes.data)
          ? collectionsRes.data
          : [];
        setCollections(collectionsArr as Collection[]);

        // Create options for collection dropdown
        const collectionOpts = collectionsArr.map((collection: Collection) => ({
          label: `${collection.name}`,
          value: collection.id,
        }));
        setCollectionOptions(collectionOpts);
      } catch (error) {
        toast({
          title: "Error",
          description: error.message ?? "Failed to fetch consumers/collections",
          variant: "destructive",
        });
      }
    };
    fetchConsumersAndCollections();
  }, []);

  // Fetch collection records with payments
  const fetchCollectionRecords = async () => {
    try {
      setLoading(true);
      const response = await api.payment.getPayments({
        page: currentPage,
        limit: paymentsPerPage,
        consumerId:
          filters.consumerId !== "all" ? filters.consumerId : undefined,
        collectionId:
          filters.collectionId !== "all" ? filters.collectionId : undefined,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        status: filters.status !== "all" ? filters.status : undefined,
      } as unknown as Parameters<typeof api.payment.getPayments>[0]);
      setCollectionRecords(response.docs);
      setTotalPages(Math.ceil(response.totalDocs / paymentsPerPage));
    } catch (error) {
      toast({
        title: "Error",
        description: error.message ?? "Failed to fetch collection records",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch collection records when filters or page changes
  useEffect(() => {
    fetchCollectionRecords();
  }, [currentPage, filters]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePrintReceipt = (payment: Payment) => {
    // TODO: Implement print functionality
    console.log("Printing receipt for:", payment.transactionNumber);
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleEditPayment = (record: CollectionRecord) => {
    setIsEditPaymentMode(true);
    setEditingCollectionRecord(record);
    // Set dummy data for the edit modal
    setSelectedCollections({
      "1": 150.0,
      "2": 75.0,
    });
    setPaymentAllocation({
      "1": 150.0,
      "2": 75.0,
    });
    setSelectedCashAccount("1");
    setSelectedBankAccount("1");
    setCashAmount(150.0);
    setBankAmount(75.0);
    setShowPaymentForm(true);
  };

  const handleViewDetails = (record: CollectionRecord) => {
    setViewingCollectionRecord(record);
    setShowViewModal(true);
  };

  const handlePaymentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement payment update functionality
    toast({
      title: "Coming Soon",
      description: "Payment editing functionality will be implemented soon.",
    });
    setShowPaymentForm(false);
    setIsEditPaymentMode(false);
    setEditingCollectionRecord(null);
    setSelectedCollections({});
    setPaymentAllocation({});
    setSelectedCashAccount(null);
    setSelectedBankAccount(null);
    setCashAmount(0);
    setBankAmount(0);
  };

  const calculateTotalSelected = () => {
    return Object.values(paymentAllocation).reduce(
      (sum, amount) => sum + (Number(amount) || 0),
      0
    );
  };

  // Date change handler
  const handleDateChange = (field: "fromDate" | "toDate", value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  // Reset filters handler
  const handleResetFilters = () => {
    setFilters(initialFilters);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      {/* <div>
        <h1 className="text-3xl font-bold tracking-tight">
          Collection Records
        </h1>
        <p className="text-muted-foreground">
          View and manage all collection records with payment details
        </p>
      </div> */}

      <Card>
        <CardHeader></CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="w-full">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-2 md:gap-4 items-end">
              {/* Consumer */}
              <div className="flex flex-col">
                <span className="text-sm font-medium mb-1">Consumer</span>
                <SingleSelect
                  options={consumerOptions}
                  selected={
                    filters.consumerId !== "all" ? [filters.consumerId] : []
                  }
                  onChange={(selected) => {
                    const value = selected.length > 0 ? selected[0] : "all";
                    handleFilterChange("consumerId", value);
                  }}
                  placeholder="Search consumer..."
                  className="w-full"
                />
              </div>
              {/* Collection */}
              <div className="flex flex-col">
                <span className="text-sm font-medium mb-1">Collection</span>
                <SingleSelect
                  options={collectionOptions}
                  selected={
                    filters.collectionId !== "all" ? [filters.collectionId] : []
                  }
                  onChange={(selected) => {
                    const value = selected.length > 0 ? selected[0] : "all";
                    handleFilterChange("collectionId", value);
                  }}
                  placeholder="Search collection..."
                  className="w-full"
                />
              </div>
              {/* Status */}
              <div className="flex flex-col">
                <span className="text-sm font-medium mb-1">Status</span>
                <Select
                  value={filters.status}
                  onValueChange={(value) => handleFilterChange("status", value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="partially_paid">
                      Partially Paid
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Billing From Date */}
              <div className="flex flex-col">
                <span className="text-sm font-medium mb-1">
                  Billing From Date
                </span>
                <Input
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) => handleDateChange("fromDate", e.target.value)}
                  className="w-full"
                  placeholder="dd-mm-yyyy"
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>
              {/* Billing To Date */}
              <div className="flex flex-col">
                <span className="text-sm font-medium mb-1">
                  Billing To Date
                </span>
                <Input
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => handleDateChange("toDate", e.target.value)}
                  className="w-full"
                  placeholder="dd-mm-yyyy"
                  min={filters.fromDate || undefined}
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>
              {/* Reset Button */}
              <div className="flex flex-col md:justify-end">
                <span className="invisible md:visible text-sm font-medium mb-1">
                  &nbsp;
                </span>
                <Button
                  variant="outline"
                  type="button"
                  onClick={handleResetFilters}
                  className="w-full"
                >
                  Reset
                </Button>
              </div>
            </div>
          </div>

          {/* Collection Records Table */}
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Consumer Name</TableHead>
                  <TableHead>Registration No</TableHead>
                  <TableHead>Contact Number</TableHead>
                  <TableHead>Collection Name</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Total Paid</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Billing Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-6">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : collectionRecords.length > 0 ? (
                  collectionRecords.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell className="font-medium">
                        {record.consumer.fullName}
                      </TableCell>
                      <TableCell>
                        {record.consumer.registrationNumber}
                      </TableCell>
                      <TableCell>{record.consumer.contactNumber}</TableCell>
                      <TableCell>{record.collection.name}</TableCell>
                      <TableCell>{record.amount.toFixed(2)}</TableCell>
                      <TableCell>{record.totalPaid.toFixed(2)}</TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                            record.status === "paid"
                              ? "bg-green-100 text-green-800"
                              : record.status === "partially_paid"
                              ? "bg-amber-100 text-amber-700"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {record.status === "partially_paid"
                            ? "Partially Paid"
                            : record.status.charAt(0).toUpperCase() +
                              record.status.slice(1)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {new Date(record.billingDate).toLocaleDateString(
                          "en-GB"
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewDetails(record)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {/* <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditPayment(record)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button> */}
                          <PrintReceiptDialog recordId={record.id} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="text-center py-6 text-muted-foreground"
                    >
                      {Object.values(filters).some((v) => v && v !== "all")
                        ? "No collection records match the filter criteria"
                        : "No collection records found"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {!loading && collectionRecords.length > 0 && (
            <div className="flex justify-center">
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add the edit payment dialog */}
      {showPaymentForm && editingCollectionRecord && (
        <Dialog open={showPaymentForm} onOpenChange={setShowPaymentForm}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="top-0 bg-background z-10 pb-4">
              <DialogTitle>Edit Collection Record</DialogTitle>
              <DialogDescription>
                Update collection record details for the selected consumer
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handlePaymentSubmit}>
              <div className="space-y-6 py-4">
                {/* Collection Record Details */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium">
                    Collection Record Details
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 border rounded-md bg-muted/50">
                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Consumer Name
                      </Label>
                      <p className="font-medium">
                        {editingCollectionRecord.consumer.fullName}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Amount
                      </Label>
                      <p className="font-medium">
                        ${editingCollectionRecord.amount.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Total Paid
                      </Label>
                      <p className="font-medium">
                        ${editingCollectionRecord.totalPaid.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Status
                      </Label>
                      <p className="font-medium capitalize">
                        {editingCollectionRecord.status}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Collection
                      </Label>
                      <p className="font-medium">
                        {editingCollectionRecord.collection.name}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Due Date
                      </Label>
                      <p className="font-medium">
                        {new Date(
                          editingCollectionRecord.dueDate
                        ).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Collections */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium">Collections</h3>
                  <div className="border rounded-md overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]">Select</TableHead>
                          <TableHead>Collection</TableHead>
                          <TableHead>Service</TableHead>
                          <TableHead>Due Amount</TableHead>
                          <TableHead>Due Date</TableHead>
                          <TableHead>Pay Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dummyCollections.map((col) => (
                          <TableRow key={col.id}>
                            <TableCell>
                              <Checkbox
                                checked={Boolean(selectedCollections[col.id])}
                                onCheckedChange={() => {
                                  setSelectedCollections((prev) => {
                                    const newSelections = { ...prev };
                                    if (newSelections[col.id]) {
                                      delete newSelections[col.id];
                                    } else {
                                      newSelections[col.id] = col.dueAmount;
                                    }
                                    return newSelections;
                                  });
                                  setPaymentAllocation((prev) => {
                                    const newAllocation = { ...prev };
                                    if (newAllocation[col.id]) {
                                      delete newAllocation[col.id];
                                    } else {
                                      newAllocation[col.id] = col.dueAmount;
                                    }
                                    return newAllocation;
                                  });
                                }}
                              />
                            </TableCell>
                            <TableCell className="font-medium">
                              {col.name}
                            </TableCell>
                            <TableCell>{col.service}</TableCell>
                            <TableCell>${col.dueAmount.toFixed(2)}</TableCell>
                            <TableCell>
                              {new Date(col.dueDate).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              {selectedCollections[col.id] !== undefined && (
                                <div className="flex items-center space-x-2">
                                  {/* <DollarSign className="h-4 w-4 text-muted-foreground" /> */}
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={paymentAllocation[col.id] || ""}
                                    onChange={(e) =>
                                      setPaymentAllocation((prev) => ({
                                        ...prev,
                                        [col.id]:
                                          parseFloat(e.target.value) || 0,
                                      }))
                                    }
                                    className="w-24"
                                  />
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Payment Account */}
                {Object.keys(selectedCollections).length > 0 && (
                  <div className="space-y-4">
                    <Separator />
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium">Payment Account</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Cash Account</Label>
                          <Select
                            value={selectedCashAccount || ""}
                            onValueChange={(value) =>
                              setSelectedCashAccount(value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select cash account" />
                            </SelectTrigger>
                            <SelectContent>
                              {dummyAccounts.cashAccounts.map((account) => (
                                <SelectItem key={account.id} value={account.id}>
                                  {account.name} (${account.balance.toFixed(2)})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {selectedCashAccount && (
                            <div className="flex items-center space-x-2 mt-2">
                              {/* <DollarSign className="h-4 w-4 text-muted-foreground" /> */}
                              <Input
                                type="number"
                                step="0.01"
                                value={cashAmount || ""}
                                onChange={(e) =>
                                  setCashAmount(parseFloat(e.target.value) || 0)
                                }
                                placeholder="Enter cash amount"
                                className="w-full"
                              />
                            </div>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label>Bank Account</Label>
                          <Select
                            value={selectedBankAccount || ""}
                            onValueChange={(value) =>
                              setSelectedBankAccount(value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select bank account" />
                            </SelectTrigger>
                            <SelectContent>
                              {dummyAccounts.bankAccounts.map((account) => (
                                <SelectItem key={account.id} value={account.id}>
                                  {account.name} (${account.balance.toFixed(2)})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {selectedBankAccount && (
                            <div className="flex items-center space-x-2 mt-2">
                              {/* <DollarSign className="h-4 w-4 text-muted-foreground" /> */}
                              <Input
                                type="number"
                                step="0.01"
                                value={bankAmount || ""}
                                onChange={(e) =>
                                  setBankAmount(parseFloat(e.target.value) || 0)
                                }
                                placeholder="Enter bank amount"
                                className="w-full"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="border rounded-md p-4 bg-muted/50">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-muted-foreground">
                              Total Selected
                            </Label>
                            <p className="text-lg font-medium">
                              ${calculateTotalSelected().toFixed(2)}
                            </p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">
                              Total Allocated
                            </Label>
                            <p className="text-lg font-medium">
                              ${(cashAmount + bankAmount).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                      {cashAmount + bankAmount !== calculateTotalSelected() && (
                        <div className="flex items-center p-3 text-sm rounded-md bg-yellow-50 border border-yellow-200 text-yellow-800">
                          <AlertCircle className="h-4 w-4 mr-2" />
                          Payment allocation ($
                          {(cashAmount + bankAmount).toFixed(2)}) does not match
                          selected dues (${calculateTotalSelected().toFixed(2)}
                          ).
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => {
                    setShowPaymentForm(false);
                    setIsEditPaymentMode(false);
                    setEditingCollectionRecord(null);
                    setSelectedCollections({});
                    setPaymentAllocation({});
                    setSelectedCashAccount(null);
                    setSelectedBankAccount(null);
                    setCashAmount(0);
                    setBankAmount(0);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    Object.keys(selectedCollections).length === 0 ||
                    calculateTotalSelected() === 0 ||
                    cashAmount + bankAmount !== calculateTotalSelected() ||
                    (!selectedCashAccount && !selectedBankAccount)
                  }
                >
                  Update Payment
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* View Details Modal */}
      {showViewModal && viewingCollectionRecord && (
        <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="top-0 bg-background z-10 pb-4">
              <DialogTitle>Collection Record Details</DialogTitle>
              <DialogDescription>
                Detailed view of collection record and payment information
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              {/* Collection Record Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">
                  Collection Record Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 border rounded-md bg-muted/50">
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Consumer Name
                    </Label>
                    <p className="font-medium">
                      {viewingCollectionRecord.consumer.fullName}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Registration Number
                    </Label>
                    <p className="font-medium">
                      {viewingCollectionRecord.consumer.registrationNumber}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Contact Number
                    </Label>
                    <p className="font-medium">
                      {viewingCollectionRecord.consumer.contactNumber}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Collection Name
                    </Label>
                    <p className="font-medium">
                      {viewingCollectionRecord.collection.name}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Entry Number
                    </Label>
                    <p className="font-medium">
                      {viewingCollectionRecord.collection.entryNumber}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Total Amount
                    </Label>
                    <p className="font-medium">
                      {viewingCollectionRecord.amount.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Total Paid
                    </Label>
                    <p className="font-medium">
                      {viewingCollectionRecord.totalPaid.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Status
                    </Label>
                    <p className="font-medium capitalize">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          viewingCollectionRecord.status === "paid"
                            ? "bg-green-100 text-green-800"
                            : viewingCollectionRecord.status ===
                              "partially_paid"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {viewingCollectionRecord.status === "partially_paid"
                          ? "Partially Paid"
                          : viewingCollectionRecord.status
                              .charAt(0)
                              .toUpperCase() +
                            viewingCollectionRecord.status.slice(1)}
                      </span>
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Billing Date
                    </Label>
                    <p className="font-medium">
                      {format(
                        new Date(viewingCollectionRecord.billingDate),
                        "dd/MM/yyyy"
                      )}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Due Date
                    </Label>
                    <p className="font-medium">
                      {format(
                        new Date(viewingCollectionRecord.dueDate),
                        "dd/MM/yyyy"
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Details */}
              {viewingCollectionRecord.payments &&
                viewingCollectionRecord.payments.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Payment Details</h3>
                    <div className="border rounded-md overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Transaction Number</TableHead>
                            <TableHead>Payment Date</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Payment Method</TableHead>
                            <TableHead>Collected By</TableHead>
                            <TableHead>Credit Account</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {viewingCollectionRecord.payments.map((payment) => (
                            <TableRow key={payment.id}>
                              <TableCell className="font-medium">
                                {payment.transactionNumber}
                              </TableCell>
                              <TableCell>
                                {format(
                                  new Date(payment.paymentDate),
                                  "dd/MM/yyyy"
                                )}
                              </TableCell>
                              <TableCell>{payment.amount.toFixed(2)}</TableCell>
                              <TableCell>{payment.paymentMethod}</TableCell>
                              <TableCell>
                                <div>
                                  <p className="font-medium">
                                    {payment.collectedBy.name}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {payment.collectedBy.mobile}
                                  </p>
                                </div>
                              </TableCell>
                              <TableCell>
                                {payment.creditAccount.name}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

              {/* No Payments Message */}
              {(!viewingCollectionRecord.payments ||
                viewingCollectionRecord.payments.length === 0) && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Payment Details</h3>
                  <div className="border rounded-md p-6 text-center text-muted-foreground">
                    <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No payment records found for this collection.</p>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowViewModal(false);
                  setViewingCollectionRecord(null);
                }}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default PaymentsPage;
